import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";

import AddAttributes from "./components/AddAttributes";
import { categoryLisitingRequest, deleteCategory } from "./redux/attributeDispatcher";
import dummyProfileImage from "../../components/assets/image/dummy-profile-image.png";
import { ApiURLs, imageBaseUrls } from "../../constant/ApiUrls";
import { useLocation, useParams } from "react-router-dom";
import { updateData } from "../../services/api";

export default function AttributesListing() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { id } = useParams();
  const categoryId = id ? id.replace(":", "") : 0;

  const categoryListing = useSelector((state) => state.categoryReducer);

  const [rows, setRows] = useState([]);

  // reorder helper
  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

   // ✅ Handle drag end
  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reordered = reorder(rows, result.source.index, result.destination.index);
    setRows(reordered);

    // Build payload for API
    const orderPayload = reordered.map((item, index) => ({
      id: item.id,
      position: index + 1, // position starts at 1
    }));

    console.log("PAYLOAD:::::::::::::::",{
          category_id: categoryId,
          order: orderPayload,
        })

    try {
      const response = await updateData({
          category_id: categoryId,
          order: orderPayload,
        },ApiURLs.REORDER_ATTRIBUTE);

      const result = await response.json();
      console.log("Reorder response:", result);

      if (!response.ok) {
        throw new Error(result.message || "Failed to update order");
      }
    } catch (err) {
      console.error("Failed to reorder attributes:", err);
    }
  };


  // Fetch category list from backend
  useEffect(() => {
    dispatch(categoryLisitingRequest(categoryId, true));
  }, [dispatch, categoryId, location.pathname]);

  // Sync redux data to local state
  useEffect(() => {
    setRows(categoryListing.categoryListing || []);
  }, [categoryListing.categoryListing]);

  // Delete handler
  const handleDelete = (row) => {
    Swal.fire({
      title: "Delete Confirmation",
      html: `Are you sure you want to delete "<b>${row.label}</b>"?`,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#4f83df",
      cancelButtonColor: "gray",
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(deleteCategory(row.id, categoryId));
      }
    });
  };

  return (
    <Paper sx={{ width: "100%", overflow: "hidden", p: 2 }}>
      {/* ✅ Category Header */}
      {categoryListing.categoryDetail && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "15px",
            backgroundColor: "#fff",
            border: "1px solid #ddd",
            borderRadius: "8px",
            margin: "0 auto 20px",
            boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
          }}
        >
          <img
            src={
              categoryListing?.categoryDetail?.image
                ? `${imageBaseUrls(categoryListing.categoryDetail.image)}`
                : dummyProfileImage
            }
            alt={categoryListing?.categoryDetail?.name || "Category"}
            style={{
              width: "100px",
              height: "100px",
              borderRadius: "8px",
              objectFit: "cover",
              marginRight: "15px",
              border: "1px solid #ddd",
            }}
          />

          <div style={{ flexGrow: 1 }}>
            <h3 style={{ margin: 0, color: "#333", fontSize: "18px" }}>
              {categoryListing?.categoryDetail?.name || "Unnamed Category"}
            </h3>
            <p style={{ color: "#666", fontSize: "14px", marginTop: 5 }}>
              {categoryListing?.categoryDetail?.description ||
                "No description available."}
            </p>
          </div>

          <AddAttributes parentId={categoryId} buttonLabel="Add Attribute" />
        </div>
      )}

      {/* ✅ Drag Drop Table */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="attributeTable">
          {(provided) => (
            <Table
              {...provided.droppableProps}
              ref={provided.innerRef}
              sx={{ minWidth: 650 }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>☰</TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>Attribute Name</TableCell>
                  <TableCell>Filters</TableCell>
                  <TableCell>Input Type</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Selection</TableCell>
                  <TableCell>Options</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <Draggable
                    key={row.id.toString()}
                    draggableId={row.id.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          bgcolor: snapshot.isDragging ? "#f0f8ff" : "white",
                          cursor: "grab",
                        }}
                      >
                        <TableCell>::</TableCell>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{row.label}</TableCell>
                        <TableCell>{row.is_filter === 1 ? "Show" : "Hide"}</TableCell>
                        <TableCell>{row.input_type || "-"}</TableCell>
                        <TableCell>{row.type || "-"}</TableCell>
                        <TableCell>
                          {row.input_type === "select"
                            ? row.is_single === 1
                              ? "Single"
                              : "Multiple"
                            : "-"}
                        </TableCell>
                        <TableCell>
                          {row.options_value
                            ? (() => {
                              try {
                                const v = JSON.parse(row.options_value);
                                return Array.isArray(v) ? v.join(", ") : "-";
                              } catch {
                                return "-";
                              }
                            })()
                            : "-"}
                        </TableCell>
                        <TableCell>
                          <div style={{ display: "flex", gap: "5px" }}>
                            <AddAttributes
                              parentId={categoryId}
                              buttonLabel="Edit"
                              editMode={true}
                              categoryData={row}
                            />


                            <button
                              onClick={() => handleDelete(row)}

                              style={{
                                backgroundColor: 'red',
                                color: "white",
                                border: "none",
                                cursor: "pointer",
                                borderRadius: "4px",
                                fontSize:  "10px" ,
                                minWidth: "60px",
                                height:  "24px",
                                padding:  "2px 5px" ,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                             Delete
                            </button>




                            {/* <Button
                              variant="contained"
                              color="error"
                              size="small"
                            >
                              Delete
                            </Button> */}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            </Table>
          )}
        </Droppable>
      </DragDropContext>
    </Paper>
  );
}
